import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, ClipboardList, User, LogOut, Menu, X, ChevronRight, Shield, ShoppingCart } from 'lucide-react';
import { useDealerAuth } from '../DealerAuthContext';
import { useDealerCart } from '../DealerCartContext';

const NAV = [
  { href: '/dealer', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { href: '/dealer/products', label: 'Product Catalog', icon: Package },
  { href: '/dealer/orders', label: 'My Orders', icon: ClipboardList },
  { href: '/dealer/profile', label: 'Account', icon: User },
];

function SidebarLink({ item, onClick }) {
  const location = useLocation();
  const isActive = item.exact ? location.pathname === item.href : location.pathname.startsWith(item.href);
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      data-testid={`dealer-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      className={`flex items-center gap-3 px-4 py-3 font-inter text-sm transition-all ${
        isActive
          ? 'bg-orange-500/10 text-orange-500 border-r-2 border-orange-500'
          : 'text-zinc-400 hover:text-white hover:bg-white/5 border-r-2 border-transparent'
      }`}
    >
      <Icon size={16} className="flex-shrink-0" />
      {item.label}
    </Link>
  );
}

export default function DealerLayout({ children }) {
  const { dealer, logout } = useDealerAuth();
  const { cartCount, setCartOpen } = useDealerCart();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex" data-testid="dealer-layout">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-60 bg-[#111111] border-r border-white/10 flex flex-col transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0`}>
        <div className="px-4 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="font-bebas text-xl text-white tracking-widest">WITT MACHINE</div>
            <div className="font-inter text-[9px] text-orange-500 tracking-widest uppercase">Dealer Portal</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-zinc-500 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(item => (
            <SidebarLink key={item.href} item={item} onClick={() => setSidebarOpen(false)} />
          ))}
        </nav>
        <div className="border-t border-white/10 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="font-inter text-xs font-medium text-white truncate">{dealer?.business_name}</p>
              <p className="font-inter text-[10px] text-zinc-600 truncate">{dealer?.email}</p>
            </div>
            <button
              data-testid="dealer-logout-button"
              onClick={logout}
              className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
              title="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </div>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 bg-[#111111] border-b border-white/10 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-zinc-400 hover:text-white" onClick={() => setSidebarOpen(true)}>
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2">
              {dealer?.status === 'approved' && (
                <span className="flex items-center gap-1 font-inter text-[10px] font-bold text-green-400 bg-green-500/10 border border-green-500/20 px-2 py-0.5 uppercase tracking-wider">
                  <Shield size={9} />Approved Dealer
                </span>
              )}
            </div>
          </div>

          {/* Cart button — always visible in header, any page */}
          <div className="flex items-center gap-2">
            <Link
              to="/dealer/products"
              onClick={() => cartCount > 0 && setCartOpen(true)}
              data-testid="header-cart-button"
              className={`flex items-center gap-2 font-bebas text-base tracking-widest px-4 py-2.5 transition-all touch-manipulation min-h-[44px] ${
                cartCount > 0
                  ? 'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white'
                  : 'border border-white/20 text-zinc-400 hover:border-orange-500/50 hover:text-white'
              }`}
            >
              <ShoppingCart size={18} />
              <span className="hidden sm:inline">Cart</span>
              {cartCount > 0 && (
                <span className="flex items-center justify-center min-w-[22px] h-[22px] px-1 bg-white text-orange-500 font-inter text-xs font-bold rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>
            <a href="/" target="_blank" className="font-inter text-zinc-700 hover:text-zinc-500 transition-colors hidden sm:block text-[10px]">
              wittmachine.com
            </a>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto" data-testid="dealer-content">
          {children}
        </main>
      </div>
    </div>
  );
}
